import { FormData } from '../types';

interface Props {
  data: Pick<FormData, 'cargo' | 'carreraDocente' | 'materiaDocente' | 'cargoAdminDetalle' | 'carreraEstudiante' | 'nivelEstudiante'>;
  onChange: (field: keyof FormData, value: string) => void;
  errors: Partial<Record<keyof FormData, string>>;
}

const CARGOS = ['Docente', 'Administrativo', 'Estudiante'];

const inputClass = (hasError?: boolean) =>
  `w-full border rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors bg-white ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`;

const cargoConfig: Record<string, { color: string; bg: string; border: string; icon: string }> = {
  Docente:       { color: 'text-blue-800',   bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'ri-presentation-line' },
  Administrativo:{ color: 'text-violet-800', bg: 'bg-violet-50', border: 'border-violet-200', icon: 'ri-user-settings-line' },
  Estudiante:    { color: 'text-emerald-800',bg: 'bg-emerald-50',border: 'border-emerald-200',icon: 'ri-book-open-line' },
};

export default function CargoYProfesionSection({ data, onChange, errors }: Props) {
  const cfg = data.cargo ? cargoConfig[data.cargo] : null;

  return (
    <section id="cargo-profesion" className="max-w-5xl mx-auto px-6 mt-6">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#0D3157] rounded-lg">
            <i className="ri-building-4-line text-white text-sm" />
          </div>
          <div>
            <h2 className="text-[#0D3157] font-bold text-base">Rol en la Universidad</h2>
            <p className="text-slate-400 text-xs">Seleccione su rol y complete la información correspondiente</p>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* ── Rol en la Universidad ── */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Rol <span className="text-amber-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {CARGOS.map((c) => {
                const isActive = data.cargo === c;
                const conf = cargoConfig[c];
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onChange('cargo', c)}
                    className={`flex flex-col items-center gap-2 py-4 px-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isActive ? `${conf.border} ${conf.bg}` : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full ${isActive ? conf.bg + ' ' + conf.border + ' border' : 'bg-slate-100'}`}>
                      <i className={`${conf.icon} text-lg ${isActive ? conf.color : 'text-slate-400'}`} />
                    </div>
                    <span className={`text-sm font-semibold ${isActive ? conf.color : 'text-slate-500'}`}>{c}</span>
                    {isActive && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${conf.bg} ${conf.color} ${conf.border} border`}>
                        Seleccionado
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {errors.cargo && <p className="text-red-500 text-xs mt-1">{errors.cargo}</p>}
          </div>

          {/* ── Sub-campos según cargo ── */}
          {cfg && (
            <div className={`rounded-lg border ${cfg.border} ${cfg.bg} p-4 space-y-4`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cargoConfig[data.cargo].icon} text-sm ${cfg.color}`} />
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider ${cfg.color}`}>
                  {data.cargo === 'Docente' && 'Detalle del rol docente'}
                  {data.cargo === 'Administrativo' && 'Detalle del cargo administrativo'}
                  {data.cargo === 'Estudiante' && 'Información académica del estudiante'}
                </span>
              </div>

              {/* DOCENTE */}
              {data.cargo === 'Docente' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Carrera <span className="text-amber-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.carreraDocente}
                      onChange={(e) => onChange('carreraDocente', e.target.value)}
                      placeholder="Escriba el nombre completo de la carrera en la que imparte clases"
                      className={inputClass(!!errors.carreraDocente)}
                    />
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-amber-600 font-medium">
                        <i className="ri-information-line mr-1" />
                        Primera letra de cada palabra en mayúscula, el resto en minúscula. Sin abreviaciones.
                      </p>
                      <p className="text-xs text-slate-400">
                        Ejemplo: <span className="font-medium text-slate-500">Medicina</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Derecho</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Ingeniería de Sistemas</span>
                      </p>
                      <p className="text-xs text-slate-400">Si imparte clases en varias carreras, sepárelas con comas.</p>
                    </div>
                    {errors.carreraDocente && <p className="text-red-500 text-xs mt-1">{errors.carreraDocente}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Materia(s) que imparte <span className="text-amber-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.materiaDocente}
                      onChange={(e) => onChange('materiaDocente', e.target.value)}
                      placeholder="Escriba la materia o materias que imparte"
                      className={inputClass(!!errors.materiaDocente)}
                    />
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-amber-600 font-medium">
                        <i className="ri-information-line mr-1" />
                        Primera letra de cada palabra en mayúscula, el resto en minúscula. Sin abreviaciones.
                      </p>
                      <p className="text-xs text-slate-400">
                        Ejemplo: <span className="font-medium text-slate-500">Metodología de la Investigación</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Anatomía</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Cálculo Integral</span>
                      </p>
                      <p className="text-xs text-slate-400">Si imparte varias materias, sepárelas con comas.</p>
                    </div>
                    {errors.materiaDocente && <p className="text-red-500 text-xs mt-1">{errors.materiaDocente}</p>}
                  </div>
                </div>
              )}

              {/* ADMINISTRATIVO */}
              {data.cargo === 'Administrativo' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                    Denominación del cargo <span className="text-amber-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={data.cargoAdminDetalle}
                    onChange={(e) => onChange('cargoAdminDetalle', e.target.value)}
                    placeholder="Escriba la denominación completa de su cargo"
                    className={inputClass(!!errors.cargoAdminDetalle)}
                  />
                  <div className="mt-1.5 space-y-0.5">
                    <p className="text-xs text-amber-600 font-medium">
                      <i className="ri-information-line mr-1" />
                      Primera letra de cada palabra en mayúscula, el resto en minúscula. Sin abreviaciones.
                    </p>
                    <p className="text-xs text-slate-400">
                      Ejemplo: <span className="font-medium text-slate-500">Director de Carrera</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Secretaria Académica</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Jefe de Tecnología</span>
                    </p>
                  </div>
                  {errors.cargoAdminDetalle && <p className="text-red-500 text-xs mt-1">{errors.cargoAdminDetalle}</p>}
                </div>
              )}

              {/* ESTUDIANTE */}
              {data.cargo === 'Estudiante' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Carrera <span className="text-amber-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.carreraEstudiante}
                      onChange={(e) => onChange('carreraEstudiante', e.target.value)}
                      placeholder="Escriba el nombre completo de su carrera"
                      className={inputClass(!!errors.carreraEstudiante)}
                    />
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-amber-600 font-medium">
                        <i className="ri-information-line mr-1" />
                        Primera letra de cada palabra en mayúscula, el resto en minúscula. Sin abreviaciones.
                      </p>
                      <p className="text-xs text-slate-400">
                        Ejemplo: <span className="font-medium text-slate-500">Medicina</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Derecho</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Ingeniería de Sistemas</span>
                      </p>
                    </div>
                    {errors.carreraEstudiante && <p className="text-red-500 text-xs mt-1">{errors.carreraEstudiante}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Año / Nivel <span className="text-amber-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.nivelEstudiante}
                      onChange={(e) => onChange('nivelEstudiante', e.target.value)}
                      placeholder="Escriba su año o nivel académico actual"
                      className={inputClass(!!errors.nivelEstudiante)}
                    />
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-amber-600 font-medium">
                        <i className="ri-information-line mr-1" />
                        Primera letra en mayúscula, el resto en minúscula. Sin abreviaciones.
                      </p>
                      <p className="text-xs text-slate-400">
                        Ejemplo: <span className="font-medium text-slate-500">Tercer año</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Quinto semestre</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Último nivel</span>
                      </p>
                    </div>
                    {errors.nivelEstudiante && <p className="text-red-500 text-xs mt-1">{errors.nivelEstudiante}</p>}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
